import type { Tone } from '../theme/tokens';

export interface AavStatusMeta {
    readonly label: string;
    readonly tone: Tone;
}

/** `statusId` of an AAV Shopify order (admin-api `/aav/orders`). */
export const AAV_ORDER_STATUSES: Readonly<Record<number, AavStatusMeta>> = Object.freeze({
    1: { label: 'Awaiting payment', tone: 'warning' },
    2: { label: 'Created', tone: 'info' },
    3: { label: 'Printed', tone: 'brand' },
    4: { label: 'Partially printed', tone: 'brand' },
    5: { label: 'Shipped', tone: 'success' },
    6: { label: 'Partially shipped', tone: 'info' },
    7: { label: 'Cancelled', tone: 'neutral' },
    8: { label: 'Removed', tone: 'danger' },
});

/** Options for a status select, in id order. */
export const AAV_ORDER_STATUS_OPTIONS: readonly { value: number; label: string }[] = Object.freeze(
    Object.entries(AAV_ORDER_STATUSES).map(([value, meta]) => ({ value: Number(value), label: meta.label }))
);

/** `statusId` of an AAV order line item. */
export const AAV_LINE_ITEM_STATUSES: Readonly<Record<number, AavStatusMeta>> = Object.freeze({
    1: { label: 'New', tone: 'info' },
    2: { label: 'Printed', tone: 'brand' },
    3: { label: 'Partially shipped', tone: 'info' },
    4: { label: 'Shipped', tone: 'success' },
    5: { label: 'Cancelled', tone: 'neutral' },
    6: { label: 'Removed', tone: 'danger' },
});

const lookup = (table: Readonly<Record<number, AavStatusMeta>>, id: unknown, fallback?: string | null): AavStatusMeta => {
    const numeric = Number(id);
    if (Number.isFinite(numeric) && table[numeric]) return table[numeric];
    return { label: fallback || 'Unknown', tone: 'neutral' };
};

/** Metadata for an order status; the backend's `status` string is the fallback label for ids the frontend does not know. */
export const aavOrderStatus = (id: unknown, fallback?: string | null): AavStatusMeta => lookup(AAV_ORDER_STATUSES, id, fallback);
export const aavLineItemStatus = (id: unknown, fallback?: string | null): AavStatusMeta => lookup(AAV_LINE_ITEM_STATUSES, id, fallback);
